import { normalizeExerciseName } from '@gymcloud/mixedscript'
import { useEffect, useId, useRef, useState } from 'react'

import { useSelectOnFocus } from '~/lib/select-on-focus'

import { FloatOut } from './float-out'

/**
 * Naming an exercise, offering the ones already known — `@specs/features/workout.spec.md`,
 * The template editor. What is typed stands on its own: a name matching nothing in the list is
 * a new exercise, and the list says so rather than leaving the silence to be read as a typo.
 */
export function ExerciseCombobox({
  value,
  names,
  onValue,
  onCommit,
  autoFocus,
}: {
  value: string
  names: readonly string[]
  onValue: (name: string) => void
  /**
   * The name as it stands once typing is over — on blur, or on an Enter that took no option.
   * `onValue` fires on every keystroke, which is too early to act on a half-typed name.
   */
  onCommit?: (name: string) => void
  /** A freshly added exercise starts in its name, ready to be typed. */
  autoFocus?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const listId = useId()
  const container = useRef<HTMLDivElement>(null)
  const select = useSelectOnFocus()

  const needle = value.trim().toLowerCase()
  // What starts with the typed text comes before what merely contains it.
  const matches = names
    .filter((name) => name.toLowerCase().includes(needle) && name.toLowerCase() !== needle)
    .sort((a, b) => {
      const first = a.toLowerCase().startsWith(needle) ? 0 : 1
      const second = b.toLowerCase().startsWith(needle) ? 0 : 1
      return first - second || a.localeCompare(b)
    })
    .slice(0, 8)
  /*
   * A name that folds to nothing — all punctuation — is every such name's twin, so the write
   * path refuses it. Offering it here would queue sets the server then drops on the floor.
   */
  const fresh =
    needle !== '' &&
    normalizeExerciseName(value) !== '' &&
    !names.some((name) => name.toLowerCase() === needle)

  // A pointer landing outside closes the list; blur would beat a click on an option to it.
  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      if (container.current && !container.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  const showing = open && (matches.length > 0 || fresh)
  const take = (name: string) => {
    onValue(name)
    onCommit?.(name)
    setOpen(false)
  }

  return (
    <div ref={container} className="relative min-w-0 flex-1">
      <input
        // The name is what the script carries, so what is typed is the value at all times.
        aria-label="Exercise"
        role="combobox"
        aria-expanded={showing}
        aria-controls={showing ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showing ? `${listId}-${String(active)}` : undefined}
        value={value}
        placeholder="Exercise"
        autoFocus={autoFocus}
        className="w-full rounded-lg bg-transparent px-2 py-1 text-base font-medium outline-none transition-colors placeholder:text-ink-muted hover:bg-hover focus:bg-surface-bright"
        onChange={(event) => {
          onValue(event.target.value)
          setActive(0)
          setOpen(true)
        }}
        onFocus={(event) => {
          select.onFocus(event)
          setOpen(true)
        }}
        onMouseUp={select.onMouseUp}
        onBlur={() => {
          setOpen(false)
          onCommit?.(value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false)
            return
          }
          if (event.key === 'Enter' && matches[active] === undefined) {
            onCommit?.(value)
            setOpen(false)
            return
          }
          if (!showing) {
            return
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive((held) => (held + 1) % matches.length)
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((held) => (held - 1 + matches.length) % matches.length)
          } else if (event.key === 'Enter') {
            const picked = matches[active]
            if (picked !== undefined) {
              event.preventDefault()
              take(picked)
            }
          }
        }}
      />

      <FloatOut open={showing}>
        <ul
          id={listId}
          role="listbox"
          aria-label="Exercises already known"
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-2xl bg-surface-overlay p-1 shadow-2xl ring-1 ring-white/10"
        >
          {matches.map((name, index) => (
            <li key={name}>
              <button
                type="button"
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === active}
                className={`menu-row ${index === active ? 'bg-white/8' : ''}`}
                onPointerDown={(event) => {
                  // The input keeps focus, so taking an option never closes the row it is in.
                  event.preventDefault()
                  take(name)
                }}
              >
                <span className="truncate">{name}</span>
              </button>
            </li>
          ))}
          {fresh ? (
            <li>
              <p className="px-3 py-2 text-xs text-ink-muted">
                “{value.trim()}” is new — its history starts here.
              </p>
            </li>
          ) : null}
        </ul>
      </FloatOut>
    </div>
  )
}
